import dayjs from "dayjs";
import { notification } from "antd";
import { formatUuidT, liftTypeLabel } from "../flatbuffers/display";
import type { LiftType } from "../flatbuffers/generated/archive/lift-type";
import type { UuidT } from "../flatbuffers/generated/front-end/uuid";
import { logicalPortMax, logicalPortMin } from "../constants/bankLogicalPort";

/**
 * One row of the deleted-banks list, read out of the decoded `PaginatedBanks`.
 *
 * The list endpoint answers with the same FlatBuffer table the live list uses,
 * so a row is a decoded bank whose `deletedAt` is filled in. Only what the
 * restore flow shows or sends is kept, which also keeps the row plain enough to
 * sit in Redux state.
 */
export interface DeletedBankRow {
  id: string;
  name: string;
  /** Lift type as the live lists label it ("ELEVATOR", "ESCALATOR", ...). */
  type: string;
  /**
   * The stored logical port. `null` only when the response carried none: 0 is a
   * real port and is never read as "unset".
   */
  logicalPort: number | null;
  /** The adapter the bank was bound to when it was deleted; "" when it had none. */
  adapterId: string;
  /** Deletion time as the server wrote it; "" when the response carried none. */
  deletedAt: string;
}

/** A page of deleted banks plus the server's total across all pages. */
export interface DeletedBankList {
  rows: DeletedBankRow[];
  total: number;
}

/** What a successful `POST /banks/:bankID/restore` reports. */
export interface RestoreBankResult {
  bankId: string;
  /** The port the bank came back with; `null` when the answer did not say. */
  logicalPort: number | null;
  /**
   * The sentence LiftCloud sends about what a restore cannot bring back (the
   * device and data-block attachments a bank delete removes for good); "" when
   * the answer carried none.
   */
  notRestored: string;
}

type Loose = Record<string, unknown>;

const asRecord = (value: unknown): Loose =>
  typeof value === "object" && value !== null ? (value as Loose) : {};

const asText = (value: unknown): string => (typeof value === "string" ? value : "");

const asNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * Both spellings are read because the FlatBuffer Object API names fields
 * camelCase while the JSON fallback names them snake_case, and which one
 * arrives depends on the transport, not on the caller.
 */
const pick = (record: Loose, camel: string, snake: string): unknown =>
  record[camel] ?? record[snake];

function toRow(raw: unknown): DeletedBankRow {
  const bank = asRecord(raw);
  const type = bank.type;
  return {
    id: formatUuidT(bank.id as UuidT | string | null | undefined),
    name: asText(bank.name),
    type: typeof type === "number" ? liftTypeLabel(type as LiftType) : asText(type),
    logicalPort: asNumber(pick(bank, "logicalPort", "logical_port")),
    adapterId: formatUuidT(pick(bank, "adapterId", "adapter_id") as UuidT | string | null),
    deletedAt: asText(pick(bank, "deletedAt", "deleted_at")),
  };
}

/**
 * Reads a decoded `GET /banks/deleted/` answer into rows and a total.
 *
 * Tolerant of a missing or malformed payload: it yields an empty list rather
 * than throwing, so a panel that has not been answered yet renders as empty.
 * The total falls back to the row count when the server sent none.
 */
export function toDeletedBankRows(payload: unknown): DeletedBankList {
  const list = asRecord(payload);
  const rows = (Array.isArray(list.data) ? list.data : []).map(toRow);
  return { rows, total: asNumber(list.total) ?? rows.length };
}

/** Reads the JSON a successful restore answers with. */
export function toRestoreBankResult(payload: unknown): RestoreBankResult {
  const answer = asRecord(payload);
  return {
    bankId: asText(answer.bank_id),
    logicalPort: asNumber(answer.logical_port),
    notRestored: asText(answer.not_restored),
  };
}

/**
 * Pulls `port` inside the range the protocol accepts, for pre-filling the field
 * that corrects a port the server would refuse. A bank with no stored port
 * starts at the protocol's lowest.
 */
export function clampLogicalPort(protocolUpper: string, port: number | null): number {
  const min = logicalPortMin(protocolUpper);
  const max = logicalPortMax(protocolUpper);
  return Math.min(max, Math.max(min, port ?? min));
}

/** Whether a failed request was refused with an HTTP 400. */
export function isBadRequest(error: unknown): boolean {
  return asRecord(error).status === 400;
}

/** `YYYY-MM-DD HH:mm` for a parseable timestamp, otherwise the text as sent. */
export function formatDeletedAt(value: string): string {
  const parsed = dayjs(value);
  return value !== "" && parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : value;
}

/** Seconds the restore notice stays up: long enough to read the limits sentence. */
const RESTORE_NOTICE_SECONDS = 15;

/**
 * Tells the operator a bank is back, and what came back without it.
 *
 * The description is LiftCloud's own `not_restored` sentence, so the limit is
 * stated where the operator is looking rather than only in the manual. It is
 * shown regardless of the "global notifications" preference: that setting
 * silences routine confirmations, and this one carries information about what
 * was lost.
 */
export function notifyBankRestored(title: string, result: RestoreBankResult, theme?: string): void {
  notification.success({
    message: title,
    description: result.notRestored === "" ? undefined : result.notRestored,
    placement: "top",
    duration: RESTORE_NOTICE_SECONDS,
    className: theme ? `monitoring__notification__${theme}` : undefined,
  });
}
